import { useState, useRef, useEffect } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { getDateFnsLocale } from '@/lib/i18n'
import { formatDistanceToNow } from 'date-fns'
import { ProjectsLayout } from '../../layouts/projects-layout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Textarea } from '../../components/ui/textarea'
import { Input } from '../../components/ui/input'
import { Tabs } from '../../components/ui/tabs'
import { Paperclip, ArrowUp, Star, Search, ArrowRight, MessageSquare, Github, Rocket, Palette, Bot } from 'lucide-react'
import { toast } from 'sonner'
import { Tooltip, TooltipTrigger, TooltipContent } from '../../components/ui/tooltip'
import { FilePreview } from '../../components/conversations/FilePreview'
import { useFileUploadWithDirectUpload } from '../../hooks/useFileUploadWithDirectUpload'
import { TemplateGrid, TemplatePreviewModal, type Template } from '../../components/templates'
import { UpgradePlanModal } from '@/components/modals/UpgradePlanModal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import { ImportFromLovableDialog } from '../../components/projects/ImportFromLovableDialog'
import { DesignConceptPicker, formatConceptName } from '../../components/projects/DesignConceptPicker'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'

interface Project {
  id: number
  name: string
  description: string | null
  slug: string
  project_type: 'rails_app' | 'django_app' | 'managed_agent'
  vps_status: string | null
  is_archived: boolean
  is_live: boolean
  is_starred: boolean
  production_url: string | null
  created_at: string
  updated_at: string
  url: string
  messages_count: number
}

interface ProjectsIndexProps {
  projects: Project[]
  active_projects: Project[]
  archived_projects: Project[]
  current_filter: 'all' | 'active' | 'archived' | 'starred'
  current_kind?: 'apps' | 'agents'
  managed_agents_enabled?: boolean
  current_sort: 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc'
  search_query: string
  starred_count: number
  archived_count: number
  templates?: Template[]
  design_concepts?: { name: string; description: string; tags: string[] }[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      organization_role?: string
      access_all_projects?: boolean
    }
  }
}

// Helper to build URL params for search/filter/sort
const buildParams = (options: {
  search?: string
  filter?: string
  sort?: string
  kind?: string
}) => {
  const params: Record<string, string> = {}
  if (options.search?.trim()) params.search = options.search.trim()
  if (options.filter && options.filter !== 'all') params.filter = options.filter
  if (options.sort && options.sort !== 'created_desc') params.sort = options.sort
  if (options.kind && options.kind !== 'apps') params.kind = options.kind
  return params
}

// Reusable ProjectCard component
interface ProjectCardProps {
  project: Project
  isStarred: boolean
  onToggleStar: (project: Project, e: React.MouseEvent) => void
  showStar?: boolean
  showLiveBadge?: boolean
  showProductionUrl?: boolean
  dimmed?: boolean
  formatStatus: (status: string | null) => string | null
  getStatusBadgeVariant: (status: string | null) => "default" | "secondary" | "destructive" | "outline"
  t: (key: string, options?: Record<string, unknown>) => string
}

function ProjectCard({
  project,
  isStarred,
  onToggleStar,
  showStar = true,
  showLiveBadge = true,
  showProductionUrl = false,
  dimmed = false,
  formatStatus,
  getStatusBadgeVariant,
  t
}: ProjectCardProps) {
  const isManagedAgent = project.project_type === 'managed_agent'

  return (
    <Card className={`group relative min-h-52 ${dimmed ? 'opacity-75' : ''}`}>
      {showStar && (
        <Button
          variant="ghost"
          size="icon"
          className={`absolute top-2 right-2 z-10 h-8 w-8 transition-all active:scale-110 ${
            isStarred
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100'
          }`}
          onClick={(e) => onToggleStar(project, e)}
        >
          <Star
            className={`h-5 w-5 ${isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
          />
        </Button>
      )}
      <Link href={project.url} className="flex flex-col h-full">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-lg flex-1">{project.name}</CardTitle>
            <div className="flex flex-wrap justify-end gap-1.5">
              {isManagedAgent && (
                <Badge variant="outline" className="shadow-sm">
                  {t('index.card.agent')}
                </Badge>
              )}
              {formatStatus(project.vps_status) && (
                <Badge variant={getStatusBadgeVariant(project.vps_status)} className="shadow-sm">
                  {formatStatus(project.vps_status)}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col flex-1">
          {project.description && (
            <p className="text-sm text-muted-foreground mb-2 line-clamp-3">
              {project.description}
            </p>
          )}
          {showProductionUrl && project.production_url && (
            <div className="flex items-center text-sm text-muted-foreground mb-2">
              <span className="font-medium mr-2">{t('index.card.url')}</span>
              <span className="truncate">{project.production_url}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm text-muted-foreground mt-auto pt-4">
            <div className="flex items-center gap-1.5">
              {showLiveBadge && !isManagedAgent && project.is_live && (
                <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
              )}
              {isManagedAgent ? <Bot className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}
              <span>{project.messages_count}</span>
            </div>
            <span>{t('index.card.updatedRelative', { time: formatDistanceToNow(new Date(project.updated_at), { addSuffix: false, locale: getDateFnsLocale() }) })}</span>
          </div>
        </CardContent>
      </Link>
    </Card>
  )
}

function Index({ projects, active_projects, archived_projects, current_filter, current_kind = 'apps', managed_agents_enabled = false, current_sort, search_query, templates = [], design_concepts = [], auth }: ProjectsIndexProps) {
  const { t } = useTranslation('projects')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchValue, setSearchValue] = useState(search_query || '')
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [djangoStack, setDjangoStack] = useState(true)  // Super admin only
  // Design concept selection
  const [selectedDesignConcept, setSelectedDesignConcept] = useState<string | null>('auto')
  const [isDesignDialogOpen, setIsDesignDialogOpen] = useState(false)

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  // Template state
  const [activeTab, setActiveTab] = useState<'projects' | 'templates'>('projects')
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)

  // Auto-open import dialog if returning from GitHub App installation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('open_import') === 'true') {
      setImportDialogOpen(true)
      params.delete('open_import')
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
    if (params.get('tab') === 'templates') {
      setActiveTab('templates')
    }
  }, [])

  useEffect(() => {
    if (current_kind === 'agents' && activeTab === 'templates') {
      setActiveTab('projects')
    }
  }, [current_kind, activeTab])

  const { organization } = usePage<{ organization?: { id: number; name: string; credits_remaining: number; current_plan: string; internal?: boolean; admin_emails: string[]; managed_agents_enabled?: boolean } }>().props

  const isSuperAdmin = auth?.user?.super_admin === true
  const isExternal = auth?.user?.organization_role === 'external'
  const canCreateProject = !isExternal || auth?.user?.access_all_projects === true
  const canCreateAgents = managed_agents_enabled || organization?.managed_agents_enabled === true
  const isCreatingAgent = canCreateAgents && current_kind === 'agents'
  const visibleTemplates = isCreatingAgent ? [] : templates
  const visibleDesignConcepts = isCreatingAgent ? [] : design_concepts
  const carouselRef = useRef<HTMLDivElement>(null)
  const isInteractingRef = useRef(false)
  const firstSetWidthRef = useRef(0)


  // Local state for optimistic star updates
  const [starredOverrides, setStarredOverrides] = useState<Record<number, boolean>>({})

  // Helper to get effective starred status (local override or server value)
  const isProjectStarred = (project: Project) => {
    return starredOverrides[project.id] ?? project.is_starred
  }

  // Measure first set width on mount
  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    // Calculate width of first set (half of total content)
    const measureWidth = () => {
      firstSetWidthRef.current = carousel.scrollWidth / 2
    }
    measureWidth()

    // Re-measure on resize
    window.addEventListener('resize', measureWidth)
    return () => window.removeEventListener('resize', measureWidth)
  }, [])

  // Auto-scroll with seamless loop
  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    let animationId: number
    const speed = 0.5

    const animate = () => {
      if (!isInteractingRef.current && firstSetWidthRef.current > 0) {
        carousel.scrollLeft += speed

        // Seamless reset: when we've scrolled past first set, jump back to start
        if (carousel.scrollLeft >= firstSetWidthRef.current) {
          carousel.scrollLeft = 0
        }
      }
      animationId = requestAnimationFrame(animate)
    }

    // Handle manual scroll - make it infinite too
    const handleScroll = () => {
      if (!isInteractingRef.current || firstSetWidthRef.current === 0) return

      // If scrolled past the first set, reset to equivalent position in first set
      if (carousel.scrollLeft >= firstSetWidthRef.current) {
        carousel.scrollLeft = carousel.scrollLeft - firstSetWidthRef.current
      }
    }

    carousel.addEventListener('scroll', handleScroll)
    animationId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(animationId)
      carousel.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const {
    selectedFiles,
    uploadedFiles,
    uploadProgress,
    allFilesUploaded,
    hasFailedUploads,
    isUploading,
    isDragging,
    fileInputRef,
    handleFileSelect,
    removeFile,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    clearFiles,
    retryUpload,
    getFileKey
  } = useFileUploadWithDirectUpload()

  // For filtered views, use the projects prop; for "all" view, use active/archived
  const isFilteredView = current_filter === 'active' || current_filter === 'archived' || current_filter === 'starred'
  const hasNoProjects = isFilteredView
    ? projects.length === 0
    : active_projects.length === 0 && archived_projects.length === 0

  // Handle search with debounce
  const handleSearch = (value: string) => {
    setSearchValue(value)

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Debounce the search
    searchTimeoutRef.current = setTimeout(() => {
      router.get('/projects', buildParams({
        search: value,
        filter: current_filter,
        sort: current_sort,
        kind: current_kind
      }), {
        preserveState: true,
        replace: true
      })
    }, 300)
  }

  // Handle sort change
  const handleSortChange = (value: string) => {
    router.get('/projects', buildParams({
      search: searchValue,
      filter: current_filter,
      sort: value,
      kind: current_kind
    }), {
      preserveState: true,
      replace: true
    })
  }

  // Handle star toggle with optimistic update
  const handleToggleStar = async (project: Project, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const currentlyStarred = isProjectStarred(project)
    const newStarred = !currentlyStarred

    // Optimistic update
    setStarredOverrides(prev => ({ ...prev, [project.id]: newStarred }))

    const url = currentlyStarred
      ? `/projects/${project.id}/unstar`
      : `/projects/${project.id}/star`

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      if (!response.ok) {
        throw new Error('Request failed')
      }
    } catch {
      // Revert on error
      setStarredOverrides(prev => ({ ...prev, [project.id]: currentlyStarred }))
      toast.error(`Failed to ${currentlyStarred ? 'unstar' : 'star'} project`)
    }
  }

  // Page title based on filter
  const getPageTitle = () => {
    const titleScope = current_kind === 'agents' ? 'agentTitles' : 'titles'
    switch (current_filter) {
      case 'active':
        return t(`index.${titleScope}.all`)
      case 'archived':
        return t(`index.${titleScope}.archived`)
      case 'starred':
        return t(`index.${titleScope}.starred`)
      default:
        return t(`index.${titleScope}.default`)
    }
  }

  const getPageDescription = () => {
    const descriptionScope = current_kind === 'agents' ? 'agentDescriptions' : 'descriptions'
    switch (current_filter) {
      case 'active':
        return t(`index.${descriptionScope}.all`)
      case 'archived':
        return t(`index.${descriptionScope}.archived`)
      case 'starred':
        return t(`index.${descriptionScope}.starred`)
      default:
        return t(`index.${descriptionScope}.default`)
    }
  }

  const getEmptyStateMessage = () => {
    const emptyScope = current_kind === 'agents' ? 'agentEmpty' : 'empty'
    switch (current_filter) {
      case 'starred':
        return t(`index.${emptyScope}.starred`)
      case 'archived':
        return t(`index.${emptyScope}.archived`)
      default:
        return t(`index.${emptyScope}.default`)
    }
  }

  const getStatusBadgeVariant = (status: string | null): "default" | "secondary" | "destructive" | "outline" => {
    if (!status) return 'secondary'

    switch (status) {
      case 'running':
        return 'default'
      case 'vps_provisioning':
        return 'secondary'
      case 'error':
        return 'destructive'
      case 'stopped':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const formatStatus = (status: string | null) => {
    if (!status) return null
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!description.trim() || isSubmitting) return

    // Check if uploads are in progress or failed
    if (selectedFiles.length > 0 && (!allFilesUploaded || hasFailedUploads)) {
      if (hasFailedUploads) {
        toast.error('Some files failed to upload. Please retry or remove them.')
      } else {
        toast.error('Please wait for files to finish uploading')
      }
      return
    }

    setIsSubmitting(true)

    // Use FormData if signed_blob_ids are present, otherwise use JSON
    if (uploadedFiles.length > 0) {
      const formData = new FormData()
      formData.append('project[description]', description.trim())
      if (isCreatingAgent) {
        formData.append('project[project_type]', 'managed_agent')
      }
      if (!isCreatingAgent && djangoStack && isSuperAdmin) {
        formData.append('project[django_stack]', 'true')
      }
      if (!isCreatingAgent && selectedDesignConcept) {
        formData.append('project[design_concept]', selectedDesignConcept)
      }
      uploadedFiles.forEach(uploadedFile => {
        formData.append('project[signed_blob_ids][]', uploadedFile.signedBlobId)
      })

      router.post('/projects', formData, {
        onFinish: () => {
          setIsSubmitting(false)
        },
        onSuccess: () => {
          setDescription('')
          setDjangoStack(false)
          clearFiles()
        }
      })
    } else {
      const projectData: { description: string; django_stack?: boolean; design_concept?: string; project_type?: 'managed_agent' } = {
        description: description.trim()
      }
      if (isCreatingAgent) {
        projectData.project_type = 'managed_agent'
      }
      if (!isCreatingAgent && djangoStack && isSuperAdmin) {
        projectData.django_stack = true
      }
      if (!isCreatingAgent && selectedDesignConcept) {
        projectData.design_concept = selectedDesignConcept
      }

      router.post('/projects', {
        project: projectData
      }, {
        onFinish: () => setIsSubmitting(false),
        onSuccess: () => {
          setDescription('')
          setDjangoStack(false)
          setSelectedDesignConcept(null)
        }
      })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!isSubmitting && description.trim()) {
        handleSubmit(e)
      }
    }
  }

  const suggestions = t(isCreatingAgent ? 'index.agentSuggestions' : 'index.suggestions', { returnObjects: true }) as { label: string; prompt: string }[]

  // Template lock check
  const isTemplateLocked = (template: Template): boolean => {
    if (isSuperAdmin) return false
    if (organization?.internal) return false
    if (template.category !== 'business') return false
    return !['pro', 'team'].includes(organization?.current_plan || '')
  }

  // Template handlers
  const handleSelectTemplate = (template: Template) => {
    if (isTemplateLocked(template)) {
      setIsUpgradeModalOpen(true)
      return
    }
    setSelectedTemplate(template)
    setTemplateModalOpen(true)
  }

  const handleUseTemplate = (template: Template) => {
    setIsCreatingFromTemplate(true)

    const projectData: { description: string; template_key: string; django_stack?: boolean; design_concept?: string } = {
      description: t('templateBrowser.defaultDescription', { templateName: template.name }),
      template_key: template.id
    }

    if (djangoStack && isSuperAdmin) {
      projectData.django_stack = true
    }
    if (selectedDesignConcept) {
      projectData.design_concept = selectedDesignConcept
    }

    router.post('/projects', {
      project: projectData
    }, {
      onFinish: () => {
        setIsCreatingFromTemplate(false)
        setTemplateModalOpen(false)
        setSelectedTemplate(null)
      },
      onError: () => {
        toast.error(t('templateBrowser.createError'))
      }
    })
  }

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="mx-auto w-full max-w-7xl flex-col gap-4 py-4 px-4 sm:px-6 md:gap-6 md:py-6">

        {/* Filtered View Header */}
        {isFilteredView && (
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-foreground">
              {getPageTitle()}
            </h2>
            <p className="text-muted-foreground mt-1">
              {getPageDescription()}
            </p>

            <div className="mt-4 flex items-center gap-3">
              <Select value={current_sort} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t('list.sort')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_desc">{t('list.sortNewest')}</SelectItem>
                  <SelectItem value="created_asc">{t('list.sortOldest')}</SelectItem>
                  <SelectItem value="name_asc">{t('list.sortName')}</SelectItem>
                  <SelectItem value="name_desc">{t('list.sortNameDesc')}</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('list.search')}
                  value={searchValue}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        )}

        {/* Filtered View Empty State */}
        {isFilteredView && hasNoProjects && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {searchValue ? t(current_kind === 'agents' ? 'index.agentEmpty.search' : 'index.empty.search', { query: searchValue }) : getEmptyStateMessage()}
            </p>
            {!searchValue && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => router.visit(current_kind === 'agents' ? '/projects?kind=agents' : '/projects')}
              >
                {t(current_kind === 'agents' ? 'index.agentEmpty.viewAll' : 'index.empty.viewAll')}
              </Button>
            )}
          </div>
        )}

        {/* Start Something New - Always visible in "all" view, hidden for external users */}
        {current_filter === 'all' && canCreateProject && (
          <div className="flex flex-col items-center py-8 mb-6">
            {/* Page Header */}
            <h1 className="text-4xl font-bold text-foreground mb-8">{t('index.hero.title')}</h1>

            {/* Input Container */}
            <form onSubmit={handleSubmit} className="w-full max-w-3xl">
              <div
                className={`relative rounded-2xl border bg-card shadow-sm transition-all ${
                  isDragging
                    ? 'border-2 border-dashed border-primary bg-primary/5'
                    : 'border-border'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {/* Drag overlay */}
                {isDragging && (
                  <div className="absolute inset-0 bg-primary/10 rounded-2xl flex items-center justify-center z-10 pointer-events-none animate-in fade-in duration-200">
                    <div className="text-center space-y-1">
                      <div className="inline-block p-2 bg-primary/10 rounded-full">
                        <Paperclip className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-xs font-medium text-primary">{t('index.dropzone.title')}</p>
                      <p className="text-[11px] text-muted-foreground">{t('index.dropzone.subtitle')}</p>
                    </div>
                  </div>
                )}

                {/* File Preview */}
                {selectedFiles.length > 0 && (
                  <div className="px-4 pt-4">
                    <FilePreview
                      files={selectedFiles}
                      uploadProgress={uploadProgress}
                      onRemove={removeFile}
                      onRetry={retryUpload}
                      getFileKey={getFileKey}
                    />
                  </div>
                )}

                {/* Input */}
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isCreatingAgent ? t('index.hero.agentPlaceholder') : t('index.hero.placeholder')}
                  disabled={isSubmitting}
                  className="min-h-[100px] max-h-[200px] resize-none border-0 bg-transparent px-5 pt-6 pb-2 text-base placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                  autoFocus
                />

                {/* Action Bar */}
                <div className="flex items-center justify-between px-5 pb-4">
                  <div className="flex items-center gap-1">
                    {/* Attach Button */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-xl text-muted-foreground hover:text-foreground"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting}
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>

                    {/* Design Style Selector */}
                    {!isCreatingAgent && visibleDesignConcepts.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground hover:text-foreground gap-1 border border-border rounded-xl hover:bg-transparent"
                        onClick={() => setIsDesignDialogOpen(true)}
                        disabled={isSubmitting}
                      >
                        <Palette className="h-4 w-4" />
                        {selectedDesignConcept
                          ? formatConceptName(selectedDesignConcept)
                          : t('settings.design.styleButton')}
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {!isCreatingAgent && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground hover:text-foreground gap-1 border border-border rounded-xl hover:bg-transparent"
                        onClick={() => setImportDialogOpen(true)}
                      >
                        <Github className="h-4 w-4" />
                        {t('index.hero.import')}
                      </Button>
                    )}

                    {/* Send Button */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="submit"
                          size="icon"
                          className="h-9 w-9 rounded-full"
                          disabled={isSubmitting || !description.trim() || isUploading || (selectedFiles.length > 0 && !allFilesUploaded)}
                        >
                          {isSubmitting ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <ArrowUp className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      {isUploading && (
                        <TooltipContent side="top">{t('index.actions.uploading')}</TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.svg,.pdf,.docx,.txt,.md,.html,.csv,.json,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
            </form>

            {/* Design Style Dialog */}
            <Dialog open={isDesignDialogOpen} onOpenChange={setIsDesignDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
                <DialogHeader>
                  <DialogTitle>{t('settings.design.dialogTitle')}</DialogTitle>
                </DialogHeader>
                <DesignConceptPicker
                  mode="select"
                  concepts={visibleDesignConcepts}
                  currentConcept={selectedDesignConcept}
                  onSelect={(conceptName) => {
                    setSelectedDesignConcept(conceptName)
                    setIsDesignDialogOpen(false)
                  }}
                />
              </DialogContent>
            </Dialog>

            {/* Suggestions Carousel - Hybrid: auto-scroll + manual scroll */}
            <div className="mt-8 w-full">
              <p className="text-sm text-muted-foreground text-center mb-4">{t('index.hero.suggestions')}</p>
              <div className="relative">
                {/* Gradient masks */}
                <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
                <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

                {/* Scrollable container */}
                <div
                  ref={carouselRef}
                  className="flex gap-3 overflow-x-auto px-4 pb-2"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  onMouseEnter={() => { isInteractingRef.current = true }}
                  onMouseLeave={() => { isInteractingRef.current = false }}
                  onTouchStart={() => { isInteractingRef.current = true }}
                  onTouchEnd={() => { isInteractingRef.current = false }}
                >
                  {/* Two copies for seamless infinite loop */}
                  {[...suggestions, ...suggestions].map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setDescription(suggestion.prompt)}
                      disabled={isSubmitting}
                      className="flex-none px-4 py-2.5 text-sm border border-border rounded-full bg-background hover:border-foreground/30 hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Import from Lovable Dialog */}
        <ImportFromLovableDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
        />

        {/* Template Preview Modal */}
        <TemplatePreviewModal
          template={selectedTemplate}
          open={templateModalOpen}
          onOpenChange={setTemplateModalOpen}
          onUseTemplate={handleUseTemplate}
          isCreating={isCreatingFromTemplate}
        />

        {/* Filtered View Projects (Live, Archived, Starred) */}
        {isFilteredView && !hasNoProjects && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                isStarred={isProjectStarred(project)}
                onToggleStar={handleToggleStar}
                showProductionUrl={true}
                dimmed={current_filter === 'archived'}
                formatStatus={formatStatus}
                getStatusBadgeVariant={getStatusBadgeVariant}
                t={t}
              />
            ))}
          </div>
        )}

        {/* All View - Tabs for Recent Projects / Templates */}
        {current_filter === 'all' && (active_projects.length > 0 || visibleTemplates.length > 0) && (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'projects' | 'templates')} className="w-full">
            {(active_projects.length > 0 || visibleTemplates.length > 0) && (
              <div className="flex gap-6 mb-6 border-b border-border">
                <button
                  onClick={() => setActiveTab('projects')}
                  className={`pb-2 text-sm font-medium transition-colors relative ${
                    activeTab === 'projects'
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {isCreatingAgent ? t('templateBrowser.tabs.recentAgents') : t('templateBrowser.tabs.recentProjects')}
                  {activeTab === 'projects' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
                  )}
                </button>
                {visibleTemplates.length > 0 && (
                  <button
                    onClick={() => setActiveTab('templates')}
                    className={`pb-2 text-sm font-medium transition-colors relative ${
                      activeTab === 'templates'
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('templateBrowser.tabs.templates')}
                    {activeTab === 'templates' && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Recent Projects Tab */}
            {activeTab === 'projects' && (
              <>
                {active_projects.length === 0 && archived_projects.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="mb-4 rounded-full bg-muted p-4">
                      <Rocket className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium">{isCreatingAgent ? t('dashboard.noAgents') : t('dashboard.noProjects')}</h3>
                    <p className="mt-1 text-sm text-muted-foreground max-w-sm">
                      {isCreatingAgent ? t('dashboard.createFirstAgent') : t('dashboard.createFirst')}
                    </p>
                  </div>
                )}
                {active_projects.length > 0 && (
                  <div className="mb-8">
                    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                      {active_projects.slice(0, 6).map((project) => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          isStarred={isProjectStarred(project)}
                          onToggleStar={handleToggleStar}
                          formatStatus={formatStatus}
                          getStatusBadgeVariant={getStatusBadgeVariant}
                          t={t}
                        />
                      ))}
                    </div>
                    {active_projects.length > 6 && (
                      <div className="mt-6 text-center">
                        <Link
                          href={current_kind === 'agents' ? '/projects?kind=agents&filter=active' : '/projects?filter=active'}
                          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        >
                          {t(current_kind === 'agents' ? 'index.card.seeAllAgents' : 'index.card.seeAll')}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* Archived Projects */}
                {archived_projects.length > 0 && (
                  <div>
                    <div className="mb-4 flex items-center">
                      <div className="flex-1 border-t border-border"></div>
                      <h3 className="px-4 text-sm font-medium text-muted-foreground">{t(current_kind === 'agents' ? 'index.card.archivedAgentsSection' : 'index.card.archivedSection')}</h3>
                      <div className="flex-1 border-t border-border"></div>
                    </div>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 opacity-75">
                      {archived_projects.map((project) => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          isStarred={isProjectStarred(project)}
                          onToggleStar={handleToggleStar}
                          showStar={false}
                          showLiveBadge={false}
                          formatStatus={formatStatus}
                          getStatusBadgeVariant={getStatusBadgeVariant}
                          t={t}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && visibleTemplates.length > 0 && (
              <TemplateGrid
                templates={visibleTemplates}
                onSelectTemplate={handleSelectTemplate}
                isTemplateLocked={isTemplateLocked}
              />
            )}
          </Tabs>
        )}

        {/* Upgrade Plan Modal */}
        <UpgradePlanModal open={isUpgradeModalOpen} onOpenChange={setIsUpgradeModalOpen} />
          </div>
        </div>
      </div>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <ProjectsLayout>{page}</ProjectsLayout>

export default Index
